import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import DevExpress from 'devextreme';
import { DataType, Format } from 'devextreme/common';
import { TableVirtualScrollDataSource } from 'ng-table-virtual-scroll';
import { ViewModelHelper } from '../shared/helpers';
import { ObjectTypeWithFilter } from '../basys-map-frame.service';

export class ComponentDataSource {
  private _filters: {};
  private _dataSource: any;
  private _columns: DataSourceColumn[];
  private _columnNames: string[];
  private _storageKey: string;
  public additionalGridContextMenu: AdditionalGridContextMenu;
  public allAutocompleteItems: string[];
  public ot: ObjectTypeWithFilter;

  get filters(): ReadonlyArray<DataSourceFilter> {
    return Object.values(this._filters);
  }
  get data(): object[] {
    return this._dataSource.filteredData;
  }
  set data(value: object[]) {
    this._dataSource.data = value;
  }
  get storageKey(): string {
    return this._storageKey;
  }
  set storageKey(value: string) {
    this._storageKey = value;
  }
  get sort(): MatSort {
    return this._dataSource.sort;
  }
  set sort(value: MatSort) {
    this._dataSource.sort = value;
  }
  get paginator(): MatPaginator {
    return this._dataSource.paginator;
  }
  set paginator(value: MatPaginator) {
    this._dataSource.paginator = value;
  }
  get isEmpty(): boolean {
    return !this._dataSource.data || this._dataSource.data.length === 0;
  }
  get columns(): ReadonlyArray<DataSourceColumn> {
    return this._columns;
  }

  //for iterations, etc.
  get internalDataSource(): any {
    return this._dataSource;
  }
  get internalColumnNames(): string[] {
    return this._columnNames;
  }

  constructor() {
    this._filters = {};
    this._dataSource = new TableVirtualScrollDataSource(null);
    this._columns = new Array<DataSourceColumn>();
    this._columnNames = new Array<string>();
    this.additionalGridContextMenu = new AdditionalGridContextMenu();

    this._dataSource.filterPredicate = (data, _) => {
      const matchingFilters: Set<DataSourceFilter> =
        new Set<DataSourceFilter>();

      for (const filter of this.filters) {
        if (filter.value) {
          const objValues = ViewModelHelper.distinctToArray(data);
          for (var i = 0; i < objValues.length; i++) {
            if (matchingFilters.size !== this.filters.length) {
              const objValue = objValues[i]?.trim()?.toLowerCase();
              const appliedFilter = filter.value?.toLowerCase();
              if (
                !matchingFilters.has(filter) &&
                objValue?.indexOf(appliedFilter) >= 0
              ) {
                matchingFilters.add(filter);
              }
            } else {
              i = objValues.length;
            }
          }
        } else if (filter.objectInfos && !matchingFilters.has(filter)) {
          const filterableObject: IFilterable = data as unknown as IFilterable;
          if (
            (filterableObject.filterableObjectId ||
              (<any>filterableObject).id) ??
            (<any>filterableObject).Id
          ) {
            const entities = filter.objectInfos.filter(
              (_) =>
                (filterableObject?.filterableObjectId &&
                  filterableObject.filterableObjectId === _.id) ||
                (!filterableObject?.filterableObjectId &&
                  ((<any>filterableObject).id ?? (<any>filterableObject).Id) ===
                    _.id),
            );
            if (entities?.length) {
              if (
                filterableObject.filterableDisplayText &&
                (!filter.displayText || filter.displayText === filter.value)
              ) {
                filter.displayText = filterableObject.filterableDisplayText;
              }
              matchingFilters.add(filter);
            }
          }
        }
      }

      return matchingFilters.size === this.filters.length;
    };
  }

  addColumn(column: DataSourceColumn) {
    this._columns.push(column);
    this._columnNames.push(column.name);
  }

  addFilter(filter: DataSourceFilter) {
    if (!this._filters[filter.key]) {
      this._filters[filter.key] = filter;
    }
    this.updateFilter();
  }

  removeFilter(filter?: DataSourceFilter) {
    if (filter) {
      if (this._filters[filter.key]) {
        delete this._filters[filter.key];
        this.updateFilter();
      }
    } else {
      this._filters = {};
      this.updateFilter();
    }
  }

  private updateFilter() {
    if (this._dataSource.filteredData) {
      //value is useless; just trigger the filter
      this._dataSource.filter = this.filters.length + '';
    }
  }
}

export interface IFilterable {
  filterableObjectId: string;
  filterableObjectType: number;
  filterableDisplayText: string;
}

export class DataSourceFilter {
  private _displayText: string;

  public static readonly MultiSelectionKey = '§§multi§§';

  private _key: string;
  value: string;
  get key(): string {
    if (this._key) {
      return this._key;
    } else if (this.value) {
      return this.value;
    } else if (this.objectInfos?.length == 1) {
      return JSON.stringify(this.objectInfos[0]);
    } else if (this.objectInfos) {
      return JSON.stringify(DataSourceFilter.MultiSelectionKey);
    }
    return '';
  }
  set key(value: string) {
    this._key = value;
  }
  get displayText(): string {
    if (this._displayText) {
      return this._displayText;
    } else if (this.value) {
      return this.value;
    }
    return '';
  }
  set displayText(value: string) {
    this._displayText = value;
  }
  objectInfos: DataEntity[];
}

export enum DataSourceColumnType {
  Databound = 1,
  Action,
}

export class DataSourceColumn {
  name: string;
  caption: string;
  allowSorting: boolean = true;
  allowHiding: boolean = true;
  allowFiltering: boolean = false;
  dataField: string;
  dataType: DataType;
  type: string;
  sortOrder: DevExpress.common.SortOrder;
  format: Format;
  width: string;
  allowGrouping: boolean;
  allowResizing: boolean;
  buttons: cellGridButton[];
  visible: boolean = true;
  showInColumnChooser: boolean = true;
  constructor() {}
}

export class cellGridButton {
  icon: string;
  hint: string;
  onClick = (row: any) => {};
}

class AdditionalGridContextMenu {
  public objectsSetsInstance: () => Promise<void>;
}
